import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';

import { AuthService } from '../../../core/services/auth.service';
import { WidgetDescriptor } from './dashboard-widget.model';

/**
 * Supplies the dashboard's widget descriptors.
 *
 * THE WIDGET-SERVICE SEAM. Today this returns a hardcoded, role-filtered array.
 * When the widget service lands, `getWidgets()` becomes a single
 * `this.http.get<WidgetDescriptor[]>('/api/dashboard/widgets')` — nothing else
 * in the dashboard changes, because every consumer already depends only on the
 * `WidgetDescriptor[]` contract. Mirrors `NavigationService.loadMenu()`.
 *
 * DEMO NOTE: data for every widget is dummy (see `dashboard-analytics.json`).
 * The catalogue spans the modules we already have; each gets a real endpoint
 * later by swapping its source in the data registry — descriptors unchanged.
 */
@Injectable({ providedIn: 'root' })
export class DashboardConfigService {
  private auth = inject(AuthService);

  /** Every known widget. The backend will own this list; here it's the stand-in. */
  private readonly catalogue: WidgetDescriptor[] = [
    // ── Tiles row ────────────────────────────────────────────────────────────
    {
      id: 'headcount',
      title: 'Total Headcount',
      subtitle: 'New hires vs leavers',
      dataSourceId: 'employee.headcount',
      representation: { type: 'stat-chart', options: ['stat-chart'] },
      chartStyle: 'line',
      chartStyleOptions: ['line', 'bar'],
      icon: 'pi pi-users',
      accent: 'blue',
      link: '/employees',
      layout: { size: 'sm', region: 'tiles', order: 10 },
    },
    {
      id: 'team-attendance',
      title: 'Team Attendance',
      subtitle: 'Present today',
      dataSourceId: 'attendance.team',
      representation: { type: 'stat-chart', options: ['stat-chart'] },
      chartStyle: 'bar',
      chartStyleOptions: ['line', 'bar'],
      icon: 'pi pi-users',
      accent: 'emerald',
      layout: { size: 'sm', region: 'tiles', order: 20 },
    },
    {
      id: 'performance',
      title: 'Performance',
      subtitle: 'Appraisal score by year',
      dataSourceId: 'performance.appraisal',
      representation: { type: 'stat-chart', options: ['stat-chart'] },
      chartStyle: 'bar',
      chartStyleOptions: ['line', 'bar'],
      icon: 'pi pi-star',
      accent: 'indigo',
      layout: { size: 'sm', region: 'tiles', order: 30 },
    },
    {
      id: 'total-absenteeism',
      title: 'Total Absenteeism',
      subtitle: 'Absent vs total headcount',
      dataSourceId: 'attendance.absenteeism',
      // Fixed donut — absent out of total headcount. No style switcher.
      representation: { type: 'stat-chart', options: ['stat-chart'] },
      chartStyle: 'donut',
      chartStyleOptions: ['donut'],
      icon: 'pi pi-user-minus',
      accent: 'rose',
      layout: { size: 'sm', region: 'tiles', order: 40 },
    },
    {
      id: 'my-attendance',
      title: 'My Attendance',
      dataSourceId: 'attendance.mine',
      representation: { type: 'number', options: ['number'] },
      icon: 'pi pi-check-circle',
      accent: 'emerald',
      link: '/attendance',
      layout: { size: 'sm', region: 'tiles', order: 50 },
    },
    {
      id: 'leave-balance',
      title: 'Leave Balance',
      dataSourceId: 'leave.balance',
      representation: { type: 'meter', options: ['meter', 'number'] },
      icon: 'pi pi-calendar-plus',
      accent: 'teal',
      link: '/leave',
      layout: { size: 'sm', region: 'tiles', order: 60 },
    },

    // ── Left column (stacked cards) ──────────────────────────────────────────
    {
      id: 'leave-summary',
      title: 'Leave Summary',
      subtitle: 'Days used vs entitlement',
      dataSourceId: 'leave.summary',
      // User can switch the rings to a bar or pie chart.
      representation: { type: 'pie', options: ['pie', 'bar', 'leave-summary'] },
      icon: 'pi pi-calendar',
      accent: 'teal',
      link: '/leave/applications',
      layout: { size: 'md', region: 'left', order: 100 },
    },
    {
      id: 'approvals',
      title: 'Pending Approvals',
      subtitle: 'Across every module',
      dataSourceId: 'approvals.pending',
      representation: { type: 'list', options: ['list'] },
      icon: 'pi pi-inbox',
      accent: 'amber',
      layout: { size: 'md', region: 'left', order: 110 },
    },
    {
  id: 'attendance-clock',
  title: 'Attendance',
  dataSourceId: 'attendance.today',
  representation: { type: 'clock',
    options: ['clock']
  },
  icon: 'pi pi-clock',
  accent: 'emerald',
  layout: {
    size: 'sm',
    region: 'right',
    order: 200
  }
},
{
  id: 'upcoming-birthdays',
  title: 'Upcoming Birthdays',
  dataSourceId: 'employee.birthdays',
  representation: {
    type: 'list',
    options: ['list']
  },
  icon: 'pi pi-gift',
  accent: 'pink',
  layout: {
    size: 'sm',
    region: 'right',
    order: 210
  }
}

    // ── Right column (reporting line, matched height) ────────────────────────
    // {
    //   id: 'reporting-line',
    //   title: 'Reporting Line',
    //   subtitle: 'Who you report to and who reports to you',
    //   dataSourceId: 'org.reporting',
    //   representation: { type: 'org', options: ['org'] },
    //   icon: 'pi pi-sitemap',
    //   accent: 'blue',
    //   layout: { size: 'lg', region: 'right', order: 200 },
    // },
  ];

  /**
   * Which widgets THIS user sees. Backend will resolve this per subscription /
   * role; here it's a role split — every widget maps to a real module in this
   * app (no predictions, no invented analytics).
   */
  getWidgets(): Observable<WidgetDescriptor[]> {
    const role = this.auth.currentUserRole();
    const roleIsHr = role === 'hr_admin' || role === 'intern_hr' || role === 'ADMIN';
    // TEMP: role check bypassed — anyone with dashboard access sees the admin
    // widget set for now. Role-based logic above is kept as-is; flip this back
    // to `roleIsHr` once we're ready to differentiate by role again.
    const isHr = true;

    // HR/manager: tiles row (workforce, team attendance, performance,
    // absenteeism); left column = leave summary + approvals; reporting on right.
    const hr = [
      'leave-summary',
      'headcount',
      'team-attendance',
      'performance',
      'total-absenteeism',
      'approvals',
      'attendance-clock',
      'upcoming-birthdays',
    ];
    // Employee: personal tiles (my attendance, leave balance, performance);
    // left = leave summary; reporting on the right.
    const employee = [
      'my-attendance',
      'leave-balance',
      'performance',
      'leave-summary',
      'attendance-clock',
      'upcoming-birthdays',
    ];
    const ids = isHr ? hr : employee;

    let widgets = this.catalogue.filter((w) => ids.includes(w.id));
    if (!isHr) {
      const EMPLOYEE_LAYOUT: Record<string, WidgetDescriptor['layout']> = {
        'my-attendance':      { size: 'sm', region: 'tiles', order: 10 },
        'leave-balance':      { size: 'sm', region: 'tiles', order: 20 },
        'performance':        { size: 'sm', region: 'tiles', order: 30 },
        'attendance-clock':   { size: 'md', region: 'left',  order: 100 },
        'leave-summary':      { size: 'md', region: 'right', order: 100 },
        'upcoming-birthdays': { size: 'md', region: 'full',  order: 120 },
      };
      widgets = widgets.map((w) =>
        EMPLOYEE_LAYOUT[w.id] ? { ...w, layout: EMPLOYEE_LAYOUT[w.id] } : w,
      );
    }

    widgets = widgets.slice().sort((a, b) => a.layout.order - b.layout.order);

    return of(widgets);
  }
}
